import { useMemo, useState } from 'react'

import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { GetServerSideProps } from 'next'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatSpotlightsList } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { checkMobile } from 'api/utils/validators'
import { Container } from 'components/atoms/container'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { StyledSpotlightsWrapper } from 'components/pages/spotlight/index.styled'
import { SpotlightCard } from 'components/pages/spotlight/spotlightCard'
import { checkAvaiableSpotlight } from 'components/pages/spotlight/utils'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { getDefaultLanguageOptions } from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS, basePath } from 'interfaces/constants'
import { FooterInfo, SpotlightSummary } from 'interfaces/types'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'

export type Props = {
  isMobileSsr: boolean
  spotlights: SpotlightSummary[]
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const SpotlightPage = ({
  spotlights,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()

  const {
    locale,
    router: { asPath },
  } = useRouterExtend()

  const [, setIsTopBarAppears] = useState(false)

  const onTopBarAppears = (b: boolean) => {
    setIsTopBarAppears(b)
  }

  const availableSpotlight = useMemo(
    () => spotlights.filter(checkAvaiableSpotlight),
    [spotlights],
  )

  return (
    <NavigatorLayout
      title={f({
        id: 'pages.spotlight.seo.title',
        defaultMessage: 'Spotlight',
      })}
      description={f({
        id: 'pages.spotlight.seo.description',
        defaultMessage: 'Spotlight',
      })}
      image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
      onTopBarAppears={onTopBarAppears}
      SEOData={{
        isIndexable: true,
      }}
      type="profile"
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
      languageOptions={getDefaultLanguageOptions(locale, asPath)}
    >
      <Container>
        <HealthToolBreadcrumb
          pageItem={[
            {
              label: f({
                id: 'categories.breadcrumb.allSpotlights',
                defaultMessage: 'All Spotlight',
              }),
              value: `${basePath}/spotlight`,
            },
          ]}
        />

        <StyledSpotlightsWrapper>
          {availableSpotlight.map((spotlight, index) => (
            <SpotlightCard
              key={index}
              index={index + 1}
              spotlight={spotlight}
              cardsPerRow={isMobile ? 2 : 4}
              buttonSize={isMobile ? 'small' : 'large'}
            />
          ))}
        </StyledSpotlightsWrapper>

        <MobileBottomNavigationWrapper />
      </Container>
    </NavigatorLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, locale } = ctx
  const userAgent = `${req.headers['user-agent']}`

  const isMobile = checkMobile(userAgent)

  const results = await Promise.all([
    callApiNew(PATHS.SPOTLIGHT.GET_ALL_SPOTLIGHT, {
      params: {
        perPage: 100,
        orderBy: 'start_date',
      },
    }),
    getFooterDataPersist(),
    getNavMenuContentPersist(locale),
  ])

  const [spotlightsRes, footerInfo, dataNavMenuContent] = results

  if (!spotlightsRes?.data || !spotlightsRes?.data?.articles) {
    return {
      notFound: true,
    }
  }

  const spotLightsFormatted = formatSpotlightsList(
    spotlightsRes?.data?.articles,
  )
  return {
    props: {
      isMobileSsr: isMobile,
      spotlights: spotLightsFormatted,
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: dataNavMenuContent || {},
    },
  }
}

export default SpotlightPage
